// ─────────────────────────────────────────────
//  CandlestickChart.js — Pure SVG candlestick
//  Renders OHLCV data with SMA 20/50/200 overlays
//  and volume bars. No external chart library needed.
// ─────────────────────────────────────────────
import React, { useMemo, useRef, useState, useCallback } from "react";
import "./CandlestickChart.css";

const PADDING = { top: 20, right: 60, bottom: 30, left: 10 };
const VOL_HEIGHT_RATIO = 0.18; // volume takes 18% of total height

function calcSMA(data, period) {
  return data.map((_, i) => {
    if (i < period - 1) return null;
    const slice = data.slice(i - period + 1, i + 1);
    return slice.reduce((s, d) => s + d.close, 0) / period;
  });
}

export default function CandlestickChart({ candles, height = 420, overlays = ["SMA20","SMA50","SMA200"] }) {
  const svgRef     = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hovIdx,  setHovIdx]  = useState(null);

  const W = "100%"; // SVG stretches to container width
  const [svgW, setSvgW] = useState(900);

  // Detect mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  // Reduce height on mobile
  const effectiveHeight = isMobile ? Math.min(height, 280) : height;

  // Measure actual pixel width
  const measRef = useCallback((node) => {
    if (node) setSvgW(node.clientWidth || 900);
  }, []);

  const { chartData, yMin, yMax, volMax, sma20, sma50, sma200 } = useMemo(() => {
    if (!candles?.length) return {};
    const closes = candles.map((c) => c.close);
    const highs   = candles.map((c) => c.high);
    const lows    = candles.map((c) => c.low);
    const vols    = candles.map((c) => c.volume || 0);
    const padding = (Math.max(...highs) - Math.min(...lows)) * 0.05;
    return {
      chartData: candles,
      yMin:    Math.min(...lows)  - padding,
      yMax:    Math.max(...highs) + padding,
      volMax:  Math.max(...vols),
      sma20:   calcSMA(candles.map((c) => ({ close: c.close })), 20),
      sma50:   calcSMA(candles.map((c) => ({ close: c.close })), 50),
      sma200:  calcSMA(candles.map((c) => ({ close: c.close })), 200),
    };
  }, [candles]);

  if (!candles?.length) {
    return <div className="cc-empty">No candlestick data available</div>;
  }

  const innerW   = svgW  - PADDING.left - PADDING.right;
  const priceH   = effectiveHeight * (1 - VOL_HEIGHT_RATIO) - PADDING.top - PADDING.bottom;
  const volH     = effectiveHeight * VOL_HEIGHT_RATIO - 8;
  const volOffY  = PADDING.top + priceH + PADDING.bottom;

  const n        = chartData.length;
  const candleW  = Math.max(1, Math.floor(innerW / n) - 1);
  const gap      = Math.max(1, Math.floor(innerW / n));

  function xPos(i)     { return PADDING.left + i * gap + gap / 2; }
  function yPrice(val) { return PADDING.top + priceH - ((val - yMin) / (yMax - yMin)) * priceH; }
  function yVol(vol)   { return volOffY + volH - (vol / volMax) * volH; }

  // Y-axis grid lines
  const gridCount = isMobile ? 4 : 5;
  const gridLines = Array.from({ length: gridCount + 1 }, (_, i) => {
    const val = yMin + ((yMax - yMin) * i) / gridCount;
    return { val, y: yPrice(val) };
  });

  // SMA path builder
  function buildPath(smaArr, yFn) {
    const pts = smaArr
      .map((v, i) => (v != null ? `${i === 0 || smaArr[i-1]==null ? "M" : "L"}${xPos(i)},${yFn(v)}` : null))
      .filter(Boolean);
    return pts.join(" ");
  }

  // Smart tooltip positioning — keeps it on screen
  function getTooltipStyle(clientX, clientY) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const ttW = 140;
    const ttH = 110;
    let left = clientX + 14;
    let top  = clientY - 80;
    if (left + ttW > vw - 8) left = clientX - ttW - 14;
    if (top < 8) top = clientY + 14;
    if (top + ttH > vh - 8) top = vh - ttH - 8;
    return { left, top };
  }

  return (
    <div className="cc-wrap" ref={measRef}>
      <svg
        ref={svgRef}
        width={W}
        height={effectiveHeight}
        className="cc-svg"
        onMouseLeave={() => { setTooltip(null); setHovIdx(null); }}
      >
        {/* ── Grid lines ── */}
        {gridLines.map(({ val, y }, i) => (
          <g key={i}>
            <line x1={PADDING.left} x2={svgW - PADDING.right} y1={y} y2={y}
              stroke="#1e2d45" strokeWidth={0.5} strokeDasharray="3 4" />
            <text x={svgW - PADDING.right + 4} y={y + 4}
              fill="#334155" fontSize={9} fontFamily="DM Mono, monospace">
              {val >= 1000 ? val.toLocaleString("en-IN",{maximumFractionDigits:0}) : val.toFixed(2)}
            </text>
          </g>
        ))}

        {/* ── Volume separator line ── */}
        <line x1={PADDING.left} x2={svgW - PADDING.right}
          y1={volOffY - 4} y2={volOffY - 4}
          stroke="#1e2d45" strokeWidth={0.5} />

        {/* ── Candles + volume bars ── */}
        {chartData.map((c, i) => {
          const isUp = c.close >= c.open;
          const color = isUp ? "#22c55e" : "#ef4444";
          const bodyTop    = yPrice(Math.max(c.open, c.close));
          const bodyBottom = yPrice(Math.min(c.open, c.close));
          const bodyH      = Math.max(1, bodyBottom - bodyTop);
          const x          = xPos(i);
          const vTop       = yVol(c.volume || 0);
          const vH         = Math.max(1, volOffY + volH - vTop);
          const isHov      = hovIdx === i;

          return (
            <g key={i}>
              {/* Hover hit area — wider on mobile for easier touch */}
              <rect
                x={x - gap / 2} y={PADDING.top} width={gap} height={priceH + PADDING.bottom + volH + 4}
                fill="transparent"
                onMouseEnter={(e) => {
                  setHovIdx(i);
                  setTooltip({ x: e.clientX, y: e.clientY, candle: c, i });
                }}
                onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                onTouchStart={(e) => {
                  const touch = e.touches[0];
                  setHovIdx(i);
                  setTooltip({ x: touch.clientX, y: touch.clientY, candle: c, i });
                }}
                onTouchMove={(e) => {
                  const touch = e.touches[0];
                  setTooltip((t) => t ? { ...t, x: touch.clientX, y: touch.clientY } : null);
                }}
                onTouchEnd={() => setTimeout(() => { setTooltip(null); setHovIdx(null); }, 1500)}
              />
              {/* Hover highlight */}
              {isHov && <line x1={x} x2={x} y1={PADDING.top} y2={volOffY + volH}
                stroke="#2d3f59" strokeWidth={1} strokeDasharray="3 3" />}
              {/* Wick */}
              <line x1={x} x2={x} y1={yPrice(c.high)} y2={yPrice(c.low)}
                stroke={color} strokeWidth={1} />
              {/* Body */}
              <rect x={x - candleW / 2} y={bodyTop} width={Math.max(1, candleW)} height={bodyH}
                fill={color} opacity={isHov ? 1 : 0.85} rx={0.5} />
              {/* Volume */}
              <rect x={x - Math.max(1, candleW * 0.8) / 2} y={vTop}
                width={Math.max(1, candleW * 0.8)} height={vH}
                fill={color} opacity={0.4} rx={0.5} />
            </g>
          );
        })}

        {/* ── SMA overlays ── */}
        {overlays.includes("SMA20")  && sma20  && (
          <path d={buildPath(sma20,  yPrice)} fill="none" stroke="#f59e0b" strokeWidth={1.2} strokeLinejoin="round" />
        )}
        {overlays.includes("SMA50")  && sma50  && (
          <path d={buildPath(sma50,  yPrice)} fill="none" stroke="#3b82f6" strokeWidth={1.2} strokeLinejoin="round" />
        )}
        {overlays.includes("SMA200") && sma200 && (
          <path d={buildPath(sma200, yPrice)} fill="none" stroke="#a855f7" strokeWidth={1.4} strokeLinejoin="round" strokeDasharray="4 2" />
        )}

        {/* ── X-axis dates ── */}
        {chartData.map((c, i) => {
          // Fewer date labels on mobile
          const step = Math.max(1, Math.floor(n / (isMobile ? 4 : 6)));
          if (i % step !== 0) return null;
          const d = new Date(c.date || c.time);
          const label = isNaN(d) ? "" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
          return (
            <text key={i} x={xPos(i)} y={effectiveHeight - 6}
              fill="#334155" fontSize={9} textAnchor="middle" fontFamily="DM Mono, monospace">
              {label}
            </text>
          );
        })}
      </svg>

      {/* ── Tooltip ── */}
      {tooltip && (
        <CandleTooltip {...tooltip} getStyle={getTooltipStyle} />
      )}

      {/* ── Legend ── */}
      <div className="cc-legend">
        {overlays.includes("SMA20")  && <span className="cc-leg" style={{ color: "#f59e0b" }}>── SMA 20</span>}
        {overlays.includes("SMA50")  && <span className="cc-leg" style={{ color: "#3b82f6" }}>── SMA 50</span>}
        {overlays.includes("SMA200") && <span className="cc-leg" style={{ color: "#a855f7" }}>- - SMA 200</span>}
        <span className="cc-leg" style={{ color: "#22c55e" }}>█ Bullish</span>
        <span className="cc-leg" style={{ color: "#ef4444" }}>█ Bearish</span>
      </div>
    </div>
  );
}

function CandleTooltip({ x, y, candle, getStyle }) {
  const style = getStyle(x, y);
  return (
    <div className="cc-tooltip" style={style}>
      <div className="cc-tt-date">{candle.date || candle.time || ""}</div>
      <div className="cc-tt-row"><span>O</span><span>₹{Number(candle.open).toFixed(2)}</span></div>
      <div className="cc-tt-row"><span>H</span><span className="green">₹{Number(candle.high).toFixed(2)}</span></div>
      <div className="cc-tt-row"><span>L</span><span className="red">₹{Number(candle.low).toFixed(2)}</span></div>
      <div className="cc-tt-row"><span>C</span><span>₹{Number(candle.close).toFixed(2)}</span></div>
      {candle.volume && <div className="cc-tt-row"><span>Vol</span><span>{Number(candle.volume).toLocaleString("en-IN")}</span></div>}
    </div>
  );
}